import type * as C from "./index";

/** Fixed actions consolidated into existing endpoints; no independent deployments. */
export interface StudioExpressionsRequest {
  accountId: string;
  animalId: string;
  personaId: string;
  requestId: string;
  prompt: string;
}
export interface StudioExpressionsResponse {
  status: string;
  letter?: string;
}

export interface EndpointRequestMap {
  world: C.ManageWorldRequest;
  worldSeed: C.ManageWorldSeedRequest;
  addRaffleEntry: C.AddRaffleEntryRequest;
  addSharedAnimal: C.AddSharedAnimalRequest;
  adminEquipItem: C.AdminEquipItemRequest;
  analyzeDocument: C.AnalyzeDocumentRequest;
  applyPersonalityHints: C.ApplyPersonalityHintsRequest;
  appointAmbassador: C.AppointAmbassadorRequest;
  generateBlogContent: C.GenerateBlogContentRequest;
  refineBlogContent: C.RefineBlogContentRequest;
  generateBlogIdeas: C.GenerateBlogIdeasRequest;
  publishBlog: C.PublishBlogRequest;
  changeOutfit: C.ChangeOutfitRequest;
  changeUserRole: C.ChangeUserRoleRequest;
  clinician: C.ClinicianRequest;
  deleteCharacter: C.DeleteCharacterRequest;
  editPersona: C.EditPersonaRequest;
  freeAccess: C.FreeAccessRequest;
  generateCraftArt: C.GenerateCraftArtRequest;
  generateCraftEngraving: C.GenerateCraftEngravingRequest;
  generateCraftXtoolProject: C.GenerateCraftXtoolProjectRequest;
  generateInsightsSummary: C.GenerateInsightsSummaryRequest;
  getRemainingTopics: C.GetRemainingTopicsRequest;
  itemPreference: C.ItemPreferenceRequest;
  photoWearable: C.PhotoWearableRequest;
  photoWearableManagement: Extract<C.PhotoWearableRequest, { action: "status" | "replace" | "accept" }>;
  manageCraftType: C.ManageCraftTypeRequest;
  streamPerformance: C.StreamPerformanceRequest;
  reassignJobAgent: C.ReassignJobAgentRequest;
  revokeAmbassador: C.RevokeAmbassadorRequest;
  startRoutineReward: C.StartRoutineRewardRequest;
  claimRoutineReward: C.ClaimRoutineRewardRequest;
  createStudioEventLink: C.CreateStudioEventLinkRequest;
  studioEventGuest: C.StudioEventGuestRequest;
  generateStudioExpressions: C.StudioExpressionsRequest;
  testManager: C.TestManagerRequest;
  uploadImage: C.UploadImageRequest;
}
export interface EndpointResponseMap {
  world: C.ManageWorldResponse;
  worldSeed: C.ManageWorldSeedResponse;
  addRaffleEntry: C.AddRaffleEntryResponse;
  addSharedAnimal: void;
  adminEquipItem: C.AdminEquipItemResponse;
  analyzeDocument: C.AnalyzeDocumentResponse;
  applyPersonalityHints: C.ApplyPersonalityHintsResponse;
  appointAmbassador: C.AppointAmbassadorResponse;
  generateBlogContent: C.GenerateBlogContentResponse;
  refineBlogContent: C.RefineBlogContentResponse;
  generateBlogIdeas: C.GenerateBlogIdeasResponse;
  publishBlog: C.PublishBlogResponse;
  changeOutfit: C.ChangeOutfitResponse;
  changeUserRole: C.ChangeUserRoleResponse;
  clinician: C.ClinicianResponse;
  deleteCharacter: C.DeleteCharacterResponse;
  editPersona: { success: boolean };
  freeAccess: C.FreeAccessResponse;
  generateCraftArt: C.GenerateCraftArtResponse;
  generateCraftEngraving: C.GenerateCraftEngravingResponse;
  generateCraftXtoolProject: C.GenerateCraftXtoolProjectResponse;
  generateInsightsSummary: C.GenerateInsightsSummaryResponse;
  getRemainingTopics: C.GetRemainingTopicsResponse;
  itemPreference: C.ItemPreferenceResponse;
  photoWearable: C.PhotoWearableStatus;
  photoWearableManagement: C.PhotoWearableStatus;
  manageCraftType: C.ManageCraftTypeResponse;
  streamPerformance: C.StreamPerformanceResponse;
  reassignJobAgent: C.ReassignJobAgentResponse;
  revokeAmbassador: C.RevokeAmbassadorResponse;
  startRoutineReward: C.RoutineRewardGoal;
  claimRoutineReward: C.RoutineRewardGoal;
  createStudioEventLink: C.CreateStudioEventLinkResponse;
  studioEventGuest: C.StudioEventGuestResponse;
  generateStudioExpressions: C.StudioExpressionsResponse;
  testManager: C.TestManagerResponse;
  uploadImage: C.UploadImageResponse;
}
export const endpointTargets = {
  world: "manageContent",
  worldSeed: "manageContent",
  addRaffleEntry: "manageContent",
  addSharedAnimal: "manageContent",
  adminEquipItem: "manage",
  analyzeDocument: "create",
  applyPersonalityHints: "create",
  appointAmbassador: "manageContent",
  generateBlogContent: "manage",
  refineBlogContent: "manage",
  generateBlogIdeas: "manage",
  publishBlog: "manage",
  changeOutfit: "manageContent",
  changeUserRole: "manageContent",
  clinician: "manageContent",
  deleteCharacter: "manageContent",
  editPersona: "manageContent",
  freeAccess: "manageContent",
  generateCraftArt: "create",
  generateCraftEngraving: "manage",
  generateCraftXtoolProject: "manage",
  generateInsightsSummary: "create",
  getRemainingTopics: "create",
  itemPreference: "manageContent",
  photoWearable: "create",
  photoWearableManagement: "manageContent",
  manageCraftType: "manage",
  streamPerformance: "create",
  reassignJobAgent: "manageContent",
  revokeAmbassador: "manageContent",
  startRoutineReward: "manageContent",
  claimRoutineReward: "manageContent",
  createStudioEventLink: "manageContent",
  studioEventGuest: "manageContent",
  generateStudioExpressions: "create",
  testManager: "manage",
  uploadImage: "manageContent",
} as const;
export type EndpointAction = keyof EndpointRequestMap;
export type CreateEndpointAction =
  | "analyzeDocument"
  | "applyPersonalityHints"
  | "generateCraftArt"
  | "generateInsightsSummary"
  | "getRemainingTopics"
  | "photoWearable"
  | "streamPerformance"
  | "generateStudioExpressions";
export type CreateEndpointRequest = {
  [A in CreateEndpointAction]: { action: A; request: EndpointRequestMap[A] };
}[CreateEndpointAction];
export type CreateEndpointResponse = EndpointResponseMap[CreateEndpointAction];
export type AdminEndpointAction =
  | "adminEquipItem"
  | "generateBlogContent"
  | "refineBlogContent"
  | "generateBlogIdeas"
  | "publishBlog"
  | "generateCraftEngraving"
  | "generateCraftXtoolProject"
  | "manageCraftType"
  | "testManager";
export type AdminEndpointRequest = {
  [A in AdminEndpointAction]: { action: A; request: EndpointRequestMap[A] };
}[AdminEndpointAction];
export type AdminEndpointResponse = EndpointResponseMap[AdminEndpointAction];
export type ContentEndpointAction =
  | "world"
  | "worldSeed"
  | "addRaffleEntry"
  | "addSharedAnimal"
  | "appointAmbassador"
  | "changeOutfit"
  | "changeUserRole"
  | "clinician"
  | "deleteCharacter"
  | "editPersona"
  | "freeAccess"
  | "itemPreference"
  | "photoWearableManagement"
  | "reassignJobAgent"
  | "revokeAmbassador"
  | "startRoutineReward"
  | "claimRoutineReward"
  | "createStudioEventLink"
  | "studioEventGuest"
  | "uploadImage";
export type ContentEndpointRequest = {
  [A in ContentEndpointAction]: { action: A; request: EndpointRequestMap[A] };
}[ContentEndpointAction];
export type ContentEndpointResponse = EndpointResponseMap[ContentEndpointAction];
